import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { PhysicsWorld } from '../physics/PhysicsWorld';
import { StateMachine } from '../ai/StateMachine';
import { ProjectilePool } from '../weapons/ProjectilePool';
import { EventBus } from '../core/EventBus';

// Physics config for realistic bike-like movement
const ENEMY_BIKE_PHYSICS = {
  maxSpeed: 28,           // 100 km/h
  acceleration: 25,       // Slightly less than player (30)
  brakeForce: 40,         // Slightly less than player (50)
  turnSpeed: 2.2,         // Slightly less than player (2.5)
  friction: 0.98,         // Same as player
  chaseSpeedMultiplier: 1.0,  // Full speed when chasing
  patrolSpeedMultiplier: 0.5, // Half speed when patrolling
  attackSpeedMultiplier: 0.7, // Medium speed during attack
  driveByDistance: 25,    // Distance for drive-by attacks
  circleDistance: 12,     // Distance for circling attacks
};

export interface EnemyBikeConfig {
  position: THREE.Vector3;
  patrolPoints: THREE.Vector3[];
  detectionRange: number;
  attackRange: number;
  speed: number;
  fireRate: number;
  damage: number;
  health: number;
}

interface EnemyContext {
  enemy: EnemyBike;
  target: THREE.Object3D | null;
  currentPatrolIndex: number;
  lastFireTime: number;
}

let enemyIdCounter = 0;

export class EnemyBike {
  public readonly id: string;
  public readonly mesh: THREE.Group;

  private config: EnemyBikeConfig;
  private body: CANNON.Body;
  private physicsWorld: PhysicsWorld;
  private stateMachine: StateMachine<EnemyContext>;
  private context: EnemyContext;
  private projectilePool: ProjectilePool;
  private playerRef: THREE.Object3D | null = null;

  private health: number;
  private isDestroyed: boolean = false;
  private currentSpeed: number = 0;

  constructor(
    scene: THREE.Scene,
    physicsWorld: PhysicsWorld,
    projectilePool: ProjectilePool,
    config: EnemyBikeConfig
  ) {
    this.id = `enemy_${enemyIdCounter++}`;
    this.config = config;
    this.physicsWorld = physicsWorld;
    this.projectilePool = projectilePool;
    this.health = config.health;

    // Create mesh
    this.mesh = this.createBikeMesh();
    this.mesh.position.copy(config.position);
    scene.add(this.mesh);

    // Set up hit detection
    this.mesh.userData = {
      id: this.id,
      health: this.health,
      hitRadius: 1.5,
      points: 300,
      isEnemy: true,
    };

    // Create physics body
    this.body = this.createBody(config.position);
    physicsWorld.addBody(this.body);

    // Create AI context
    this.context = {
      enemy: this,
      target: null,
      currentPatrolIndex: 0,
      lastFireTime: 0,
    };

    // Set up state machine
    this.stateMachine = this.createStateMachine();
    this.stateMachine.setState('patrol');

    // Listen for damage
    EventBus.on('target:damaged', (data) => {
      if (data.id === this.id) {
        this.health = data.healthRemaining;
        this.mesh.userData.health = this.health;
        this.onDamaged();
      }
    });

    EventBus.on('target:destroyed', (data) => {
      if (data.id === this.id) {
        this.destroy(scene);
      }
    });
  }

  private createBikeMesh(): THREE.Group {
    const group = new THREE.Group();

    // Body - enemy bikes are red/orange themed
    const bodyGeometry = new THREE.BoxGeometry(0.5, 0.3, 1.4);
    const bodyMaterial = new THREE.MeshStandardMaterial({
      color: 0x2e1a1a,
      metalness: 0.9,
      roughness: 0.2,
    });
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    body.position.y = 0.3;
    group.add(body);

    // Neon trim - orange/red
    const trimGeometry = new THREE.BoxGeometry(0.52, 0.05, 1.42);
    const trimMaterial = new THREE.MeshStandardMaterial({
      color: 0xff4400,
      emissive: 0xff4400,
      emissiveIntensity: 2,
    });
    const topTrim = new THREE.Mesh(trimGeometry, trimMaterial);
    topTrim.position.y = 0.475;
    group.add(topTrim);

    // Side trims
    const sideTrimGeometry = new THREE.BoxGeometry(0.02, 0.15, 1.2);
    const sideTrimMaterial = new THREE.MeshStandardMaterial({
      color: 0xff0000,
      emissive: 0xff0000,
      emissiveIntensity: 2,
    });

    const leftTrim = new THREE.Mesh(sideTrimGeometry, sideTrimMaterial);
    leftTrim.position.set(-0.26, 0.3, 0);
    group.add(leftTrim);

    const rightTrim = new THREE.Mesh(sideTrimGeometry, sideTrimMaterial.clone());
    rightTrim.position.set(0.26, 0.3, 0);
    group.add(rightTrim);

    // Headlight - red for enemy
    const headlightGeometry = new THREE.CircleGeometry(0.08, 16);
    const headlightMaterial = new THREE.MeshStandardMaterial({
      color: 0xff0000,
      emissive: 0xff0000,
      emissiveIntensity: 3,
    });
    const headlight = new THREE.Mesh(headlightGeometry, headlightMaterial);
    headlight.position.set(0, 0.35, 0.7);
    group.add(headlight);

    // Engine glow
    const engineGlowGeometry = new THREE.BoxGeometry(0.3, 0.02, 0.6);
    const engineGlowMaterial = new THREE.MeshStandardMaterial({
      color: 0xff4400,
      emissive: 0xff4400,
      emissiveIntensity: 1.5,
    });
    const engineGlow = new THREE.Mesh(engineGlowGeometry, engineGlowMaterial);
    engineGlow.position.set(0, 0.12, -0.2);
    group.add(engineGlow);

    return group;
  }

  private createBody(position: THREE.Vector3): CANNON.Body {
    const shape = new CANNON.Box(new CANNON.Vec3(0.5, 0.4, 1.0));
    const body = new CANNON.Body({
      mass: 200,
      shape: shape,
      position: new CANNON.Vec3(position.x, position.y + 0.5, position.z),
      angularDamping: 0.99,
      linearDamping: 0.5,
    });
    body.angularFactor = new CANNON.Vec3(0, 1, 0);
    return body;
  }

  private createStateMachine(): StateMachine<EnemyContext> {
    const sm = new StateMachine<EnemyContext>(this.context);

    // Patrol state - cruising at half speed
    sm.addState({
      name: 'patrol',
      onUpdate: (ctx, dt) => {
        this.moveTowardsPoint(
          this.config.patrolPoints[ctx.currentPatrolIndex],
          dt,
          ENEMY_BIKE_PHYSICS.patrolSpeedMultiplier
        );

        // Check if reached patrol point (use wider radius for smoother turns)
        const distance = this.mesh.position.distanceTo(this.config.patrolPoints[ctx.currentPatrolIndex]);
        if (distance < 8) {
          ctx.currentPatrolIndex = (ctx.currentPatrolIndex + 1) % this.config.patrolPoints.length;
        }

        // Look for player
        ctx.target = this.findTarget();
      },
    });

    // Chase state - aggressive pursuit with intercept prediction
    sm.addState({
      name: 'chase',
      onUpdate: (ctx, dt) => {
        if (!ctx.target) return;

        // Intercept prediction: aim ahead of the player
        const targetPos = ctx.target.position.clone();

        // Get player velocity if available
        const playerVelocity = (ctx.target.userData as Record<string, THREE.Vector3>)?.velocity;
        if (playerVelocity) {
          // Predict where player will be in ~1.5 seconds
          const predictionTime = 1.5;
          targetPos.add(playerVelocity.clone().multiplyScalar(predictionTime));
        }

        // Full throttle chase - max speed
        this.moveTowardsPoint(targetPos, dt, ENEMY_BIKE_PHYSICS.chaseSpeedMultiplier);
      },
    });

    // Attack state
    sm.addState({
      name: 'attack',
      onUpdate: (ctx, dt) => {
        if (!ctx.target) return;

        // Strafe around target
        this.strafeAroundTarget(ctx.target.position, dt);
        this.tryFire(ctx);
      },
    });

    // Transitions
    sm.addTransition('patrol', 'chase', (ctx) => {
      return ctx.target !== null && this.getDistanceToTarget(ctx.target) <= this.config.detectionRange;
    });

    sm.addTransition('chase', 'attack', (ctx) => {
      if (!ctx.target) return false;
      return this.getDistanceToTarget(ctx.target) <= this.config.attackRange;
    });

    sm.addTransition('chase', 'patrol', (ctx) => {
      return ctx.target === null || this.getDistanceToTarget(ctx.target) > this.config.detectionRange;
    });

    sm.addTransition('attack', 'chase', (ctx) => {
      if (!ctx.target) return true;
      return this.getDistanceToTarget(ctx.target) > this.config.attackRange;
    });

    sm.addTransition('attack', 'patrol', (ctx) => {
      return ctx.target === null || this.getDistanceToTarget(ctx.target) > this.config.detectionRange;
    });

    return sm;
  }

  public setPlayer(player: THREE.Object3D): void {
    this.playerRef = player;
  }

  private findTarget(): THREE.Object3D | null {
    if (!this.playerRef) return null;
    const distance = this.mesh.position.distanceTo(this.playerRef.position);
    return distance <= this.config.detectionRange ? this.playerRef : null;
  }

  private getDistanceToTarget(target: THREE.Object3D): number {
    return this.mesh.position.distanceTo(target.position);
  }

  private moveTowardsPoint(point: THREE.Vector3, dt: number, speedMultiplier: number = 1): void {
    const direction = new THREE.Vector3()
      .subVectors(point, this.mesh.position);
    direction.y = 0;

    const distance = direction.length();
    if (distance < 0.5) return;

    direction.normalize();

    // Calculate target angle and current angle
    const targetAngle = Math.atan2(direction.x, direction.z);
    let angleDiff = targetAngle - this.mesh.rotation.y;

    // Normalize angle difference
    while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
    while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;

    // Speed-dependent steering (faster = slower turn rate, like real bikes)
    const speedRatio = this.currentSpeed / ENEMY_BIKE_PHYSICS.maxSpeed;
    const speedFactor = Math.max(0.3, 1 - speedRatio * 0.5);
    const turnAmount = Math.sign(angleDiff) * Math.min(
      Math.abs(angleDiff),
      ENEMY_BIKE_PHYSICS.turnSpeed * speedFactor * dt
    );
    this.mesh.rotation.y += turnAmount;

    // Calculate target speed based on multiplier
    const targetSpeed = ENEMY_BIKE_PHYSICS.maxSpeed * speedMultiplier;

    // Brake if facing wrong way or need sharp turn (>60 degrees)
    if (Math.abs(angleDiff) > Math.PI / 3) {
      // Apply braking for sharp turns
      this.currentSpeed = Math.max(0, this.currentSpeed - ENEMY_BIKE_PHYSICS.brakeForce * dt);
    } else {
      // Accelerate toward target speed
      if (this.currentSpeed < targetSpeed) {
        this.currentSpeed = Math.min(
          targetSpeed,
          this.currentSpeed + ENEMY_BIKE_PHYSICS.acceleration * dt
        );
      } else {
        // Apply friction when above target speed
        this.currentSpeed *= ENEMY_BIKE_PHYSICS.friction;
      }
    }

    // Apply velocity in facing direction
    const forward = new THREE.Vector3(0, 0, 1);
    forward.applyQuaternion(this.mesh.quaternion);

    this.body.velocity.x = forward.x * this.currentSpeed;
    this.body.velocity.z = forward.z * this.currentSpeed;
  }

  private strafeAroundTarget(targetPos: THREE.Vector3, dt: number): void {
    const toTarget = new THREE.Vector3()
      .subVectors(targetPos, this.mesh.position);
    toTarget.y = 0;
    const distanceToTarget = toTarget.length();

    // Mixed attack mode: drive-by at distance, circle when close
    if (distanceToTarget > ENEMY_BIKE_PHYSICS.driveByDistance) {
      // Drive-by attack: approach target while firing
      this.moveTowardsPoint(targetPos, dt, ENEMY_BIKE_PHYSICS.attackSpeedMultiplier);
    } else if (distanceToTarget > ENEMY_BIKE_PHYSICS.circleDistance) {
      // Mid-range: aggressive circling with speed
      const perpendicular = new THREE.Vector3(-toTarget.z, 0, toTarget.x).normalize();
      const targetPoint = this.mesh.position.clone()
        .add(perpendicular.multiplyScalar(15))
        .add(toTarget.normalize().multiplyScalar(-5)); // Slight retreat while circling

      this.moveTowardsPoint(targetPoint, dt, ENEMY_BIKE_PHYSICS.attackSpeedMultiplier);
    } else {
      // Close range: tight circling while facing target
      const toTargetNorm = toTarget.clone().normalize();
      const targetAngle = Math.atan2(toTargetNorm.x, toTargetNorm.z);

      // Smooth rotation towards target
      let angleDiff = targetAngle - this.mesh.rotation.y;
      while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
      while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;
      this.mesh.rotation.y += angleDiff * dt * 5;

      // Strafe perpendicular while maintaining some speed
      const strafeDir = new THREE.Vector3(-toTarget.z, 0, toTarget.x).normalize();
      const targetSpeed = ENEMY_BIKE_PHYSICS.maxSpeed * 0.4;

      // Gradual speed adjustment
      if (this.currentSpeed < targetSpeed) {
        this.currentSpeed = Math.min(targetSpeed, this.currentSpeed + ENEMY_BIKE_PHYSICS.acceleration * 0.5 * dt);
      } else {
        this.currentSpeed *= 0.95;
      }

      this.body.velocity.x = strafeDir.x * this.currentSpeed;
      this.body.velocity.z = strafeDir.z * this.currentSpeed;
    }
  }

  private tryFire(ctx: EnemyContext): void {
    const now = performance.now();
    const fireInterval = 1000 / this.config.fireRate;

    if (now - ctx.lastFireTime < fireInterval) return;

    ctx.lastFireTime = now;

    const forward = new THREE.Vector3(0, 0, 1);
    forward.applyQuaternion(this.mesh.quaternion);

    const firePos = this.mesh.position.clone();
    firePos.y += 0.5;
    firePos.add(forward.clone().multiplyScalar(1));

    this.projectilePool.spawn(
      firePos,
      forward,
      100,
      this.config.damage,
      new THREE.Color(0xff4400),
      2
    );
  }

  private onDamaged(): void {
    // Flash white
    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const material = child.material as THREE.MeshStandardMaterial;
        if (material.emissive) {
          const original = material.emissive.clone();
          material.emissive.setHex(0xffffff);
          setTimeout(() => material.emissive.copy(original), 100);
        }
      }
    });
  }

  private destroy(scene: THREE.Scene): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    // Create explosion
    this.createExplosion(scene);

    scene.remove(this.mesh);
    this.physicsWorld.removeBody(this.body);
  }

  private createExplosion(scene: THREE.Scene): void {
    const position = this.mesh.position.clone();

    for (let i = 0; i < 12; i++) {
      const size = 0.15 + Math.random() * 0.3;
      const geometry = new THREE.BoxGeometry(size, size, size);
      const material = new THREE.MeshStandardMaterial({
        color: Math.random() > 0.5 ? 0xff4400 : 0xff0000,
        emissive: Math.random() > 0.5 ? 0xff4400 : 0xff0000,
        emissiveIntensity: 2,
        transparent: true,
      });
      const debris = new THREE.Mesh(geometry, material);

      debris.position.copy(position);
      debris.userData.velocity = new THREE.Vector3(
        (Math.random() - 0.5) * 12,
        3 + Math.random() * 8,
        (Math.random() - 0.5) * 12
      );

      scene.add(debris);

      const animate = () => {
        debris.userData.velocity.y -= 15 * 0.016;
        debris.position.add(debris.userData.velocity.clone().multiplyScalar(0.016));
        debris.rotation.x += 0.15;
        debris.rotation.z += 0.1;
        material.opacity -= 0.025;

        if (debris.position.y < 0 || material.opacity <= 0) {
          scene.remove(debris);
        } else {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }

  public update(dt: number): void {
    if (this.isDestroyed) return;

    // Sync mesh with physics
    this.mesh.position.set(this.body.position.x, this.body.position.y, this.body.position.z);

    // Keep upright
    this.body.quaternion.setFromAxisAngle(new CANNON.Vec3(0, 1, 0), this.mesh.rotation.y);

    // Update AI
    this.context.target = this.findTarget();
    this.stateMachine.update(dt);
  }

  public getMesh(): THREE.Group {
    return this.mesh;
  }

  public isAlive(): boolean {
    return !this.isDestroyed;
  }

  public getState(): string | null {
    return this.stateMachine.getCurrentStateName();
  }
}
